import i18n from "@client-shared/lib/i18n";
import type { InstallShopifyServiceParams } from "@client-shared/services/api";
import * as api from "@client-shared/services/api";
import { SHOPIFY_CLIENT_ERROR_MESSAGES } from "@shared/constants";

export const INSTALL_SHOPIFY_ERROR_MESSAGES = {
  INVALID_ACCESS_TOKEN_OR_SCOPE: "Invalid accessToken or scope",
  INVALID_PARAM: "Invalid shop param",
  WEBHOOK_REGISTRATION_FAILED: "Shopify webhook registration failed",
  SESSION_EXISTS_FOR_DOMAIN: "Session for the given domain already exists.",
  MISSING_ACCESS_SCOPES: "Missing access scopes:",
  INVALID_CLIENT_CREDENTIALS: "Failed to obtain access token using clientId, clientSecret and code",
  INVALID_CLIENT_CREDENTIALS_ENCRYPTED: "Failed to obtain Shopify access token",
};

const EXACT_MATCH_ERROR_KEYS: ReadonlyArray<{ match: string; key: string }> = [
  { match: SHOPIFY_CLIENT_ERROR_MESSAGES.INVALID_SHOP, key: "invalidShop" },
  { match: INSTALL_SHOPIFY_ERROR_MESSAGES.INVALID_ACCESS_TOKEN_OR_SCOPE, key: "invalidAccessTokenOrScope" },
  { match: INSTALL_SHOPIFY_ERROR_MESSAGES.INVALID_PARAM, key: "invalidShopParam" },
  { match: INSTALL_SHOPIFY_ERROR_MESSAGES.WEBHOOK_REGISTRATION_FAILED, key: "webhookRegistrationFailed" },
  { match: INSTALL_SHOPIFY_ERROR_MESSAGES.SESSION_EXISTS_FOR_DOMAIN, key: "sessionExistsForDomain" },
];

const buildInstallShopifyError = (message: string): Error => {
  // Checked first because it's the most specific case: the backend attaches this as a
  // trailing detail on "Invalid accessToken or scope" (see the prefix match below), and
  // the scope list it carries is more actionable than that generic message.
  if (message.includes(INSTALL_SHOPIFY_ERROR_MESSAGES.MISSING_ACCESS_SCOPES)) {
    const missingScopes = message.substring(message.lastIndexOf(":") + 1).trim();
    return new Error(i18n.t("components.ModalServiceForm.installShopifyService.missingAccessScopes", { missingScopes }));
  }
  // Prefix match, not exact equality: the backend sometimes appends a non-scope detail to
  // "Invalid accessToken or scope" (e.g. "Invalid accessToken or scope. Shopify API unavailable"
  // when fetching the shop's granted scopes fails outright) — that should still classify by
  // its known leading message instead of falling through to the generic error below.
  const exact = EXACT_MATCH_ERROR_KEYS.find((entry) => message.startsWith(entry.match));
  if (exact) {
    return new Error(i18n.t(`components.ModalServiceForm.installShopifyService.${exact.key}`));
  }
  if (
    message.includes(INSTALL_SHOPIFY_ERROR_MESSAGES.INVALID_CLIENT_CREDENTIALS) ||
    message.includes(INSTALL_SHOPIFY_ERROR_MESSAGES.INVALID_CLIENT_CREDENTIALS_ENCRYPTED)
  ) {
    return new Error(i18n.t("components.ModalServiceForm.installShopifyService.invalidClientCredentials"));
  }
  return new Error(i18n.t("components.ModalServiceForm.installShopifyService.general"));
};

export const installShopifyService = async (params: InstallShopifyServiceParams) => {
  try {
    return await api.installShopifyService(params);
  } catch (e) {
    const message = e instanceof Error ? e.message : "";
    throw buildInstallShopifyError(message);
  }
};

/**
 * Installs a WooCommerce service and returns the JWT token.
 */
export const installWooCommerceService = async (shop: string, locale?: string) => {
  try {
    return await api.installWooCommerceService(shop, locale);
  } catch (e) {
    const message = e instanceof Error ? e.message : "";
    throw new Error(message);
  }
};
